import {
    ICompany,
    ITag,
    IServerCompany,
    IServerCompanySettings,
    ICompanySettings,
    IServerTag,
    IServerCompanyMailbox,
    ICompanyMailbox,
    IServerCompanyMailboxUser,
    ICompanyMailboxUser,
    IServerMailboxConfiguration,
    IMailboxConfiguration,
    IServerEmailSchedulingSettings,
    IEmailSchedulingSettings,
    IMailboxConfigurationUpdate,
} from '../../../common';

export const getCompanyInfoFromServerFormat = (
    companyInfo: IServerCompany
): ICompany => {
    return {
        companyLogoUrl: companyInfo.company_logo_url || '',
        defaultLanguage: companyInfo.default_language || '',
        description: companyInfo.description || '',
        id: companyInfo.id || 0,
        name: companyInfo.name || '',
        key: companyInfo.key || '',
        features: companyInfo.features || [],
        addressText: companyInfo.address_text || '',
        url: companyInfo.url || '',
        phone: companyInfo.phone || '',
    };
};

export const convertServerTagsToClient = (
    serverTags: Array<ITag>
): Array<ITag> => {
    return serverTags.map((tag) => ({
        tag_id: tag.id || '',
        label: tag.label,
        color: tag.color,
    }));
};

const convertCompanyMailboxUserFromServer = (
    serverUser: IServerCompanyMailboxUser
): ICompanyMailboxUser => {
    return {
        id: serverUser.id,
        firstName: serverUser.first_name,
        lastName: serverUser.last_name,
        email: serverUser.email,
    };
};

const convertCompanyMailboxFromServer = (
    serverMailbox: IServerCompanyMailbox
): ICompanyMailbox => {
    return {
        id: serverMailbox.id,
        emailAddress: serverMailbox.email_address,
        createdBy: convertCompanyMailboxUserFromServer(
            serverMailbox.created_by
        ),
        createdAt: serverMailbox.created_at,
        name: serverMailbox.name,
        status: serverMailbox.status,
        provider: serverMailbox.provider,
    };
};

const convertMailboxConfigurationFromServer = (
    serverConfig: IServerMailboxConfiguration
): IMailboxConfiguration => {
    return {
        id: serverConfig.id,
        mailbox: serverConfig.mailbox,
        mailboxType: serverConfig.mailbox_type,
        mailboxSelection: serverConfig.mailbox_selection,
    };
};

const convertEmailSchedulingSettingsFromServer = (
    serverSettings: IServerEmailSchedulingSettings
): IEmailSchedulingSettings => {
    return {
        id: serverSettings.id,
        delayHours: serverSettings.delay_hours,
        startTime: serverSettings.start_time,
        endTime: serverSettings.end_time,
        allowedDays: serverSettings.allowed_days,
    };
};

export const getCompanySettingsFromServerFormat = (
    companySettings: IServerCompanySettings
): ICompanySettings => {
    return {
        id: companySettings.id,
        companyInfo: getCompanyInfoFromServerFormat(
            companySettings.company_info
        ),
        emailSchedulingSettings: companySettings.email_scheduling_settings
            ? convertEmailSchedulingSettingsFromServer(
                  companySettings.email_scheduling_settings
              )
            : null,
        rejectReasons: companySettings.reject_reasons || [],
        mailboxConfiguration: (companySettings.mailbox_configuration || []).map(
            convertMailboxConfigurationFromServer
        ),
        companyMailboxes: (companySettings.company_mailboxes || []).map(
            convertCompanyMailboxFromServer
        ),
        primaryColor: companySettings.primary_color || null,
        secondaryColor: companySettings.secondary_color || null,
    };
};

export const getNewTagOrUpdatedTagFromServerFormat = (
    serverTag: IServerTag
): ITag => {
    return {
        id: null,
        tag_id: serverTag.id,
        label: serverTag.label,
        color: serverTag.color,
    };
};

export const convertCompanyInfoToServerFormat = (data: {
    description?: string;
    phoneNumber?: string;
    address?: string;
    website?: string;
    logoFile?: File | null;
    mailboxConfiguration?: Array<IMailboxConfigurationUpdate>;
    primaryColor?: string | null;
    secondaryColor?: string | null;
}): FormData | object => {
    const hasFile = data.logoFile instanceof File;

    // If there's a file, use FormData for multipart upload
    if (hasFile) {
        const formData = new FormData();

        // Build the company_info fields
        const companyInfoData: Record<string, string> = {};
        if (data.description !== undefined) {
            companyInfoData.description = data.description;
        }
        if (data.phoneNumber !== undefined) {
            companyInfoData.phone = data.phoneNumber;
        }
        if (data.address !== undefined) {
            companyInfoData.address_text = data.address;
        }
        if (data.website !== undefined) {
            companyInfoData.url = data.website;
        }

        // Add logo file
        if (data.logoFile) {
            formData.append(
                'company_info.logo.file',
                data.logoFile,
                data.logoFile.name
            );
        }

        // Add text fields to FormData
        Object.keys(companyInfoData).forEach((key) => {
            formData.append(`company_info.${key}`, companyInfoData[key]);
        });

        // Add color fields if present
        if (data.primaryColor !== undefined && data.primaryColor !== null) {
            formData.append('primary_color', data.primaryColor);
        }
        if (data.secondaryColor !== undefined && data.secondaryColor !== null) {
            formData.append('secondary_color', data.secondaryColor);
        }

        // Add mailbox configuration if present
        if (data.mailboxConfiguration) {
            formData.append(
                'mailbox_configuration',
                JSON.stringify(data.mailboxConfiguration)
            );
        }

        return formData;
    }

    // Regular JSON payload if no file
    const payload: Record<string, any> = {};
    
    const companyInfo: Record<string, string> = {};
    if (data.description !== undefined) {
        companyInfo.description = data.description;
    }
    if (data.phoneNumber !== undefined) {
        companyInfo.phone = data.phoneNumber;
    }
    if (data.address !== undefined) {
        companyInfo.address_text = data.address;
    }
    if (data.website !== undefined) {
        companyInfo.url = data.website;
    }

    if (Object.keys(companyInfo).length > 0) {
        payload.company_info = companyInfo;
    }

    // Add color fields if present
    if (data.primaryColor !== undefined && data.primaryColor !== null) {
        payload.primary_color = data.primaryColor;
    }
    if (data.secondaryColor !== undefined && data.secondaryColor !== null) {
        payload.secondary_color = data.secondaryColor;
    }

    // Add mailbox configuration if present
    if (data.mailboxConfiguration) {
        payload.mailbox_configuration = data.mailboxConfiguration;
    }

    return payload;
};
